import {
  Avatar,
  Box,
  BoxProps,
  Button,
  Divider,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorMode,
  useColorModeValue,
  useDisclosure,
  VStack
} from '@chakra-ui/react'
import { Heart, PlusIcon, Star } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FiGrid, FiMessageSquare, FiSearch } from 'react-icons/fi'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { useAgents } from '../../hooks/agents/useAgents'
import { CLIENT_CONFIG } from '../../core/client/buildConfig'

import { useInView } from 'react-intersection-observer'
import { useChats } from '../../hooks/chat/useChats'
import { useLanguage } from '../../hooks/utility/useLanguage'
import useMobile from '../../hooks/utility/useMobile'
import { ChatSummarySchema } from '../../types/chatTypes'
import { getIconByName } from '../../utils/iconUtils'
import { getFromSessionStorage, setToSessionStorage } from '../../utils/sessionStorage'
import SidebarItem from './SidebarItem'
import SidebarSection from './SidebarSection'
import SidebarToggleButton from './SidebarToggleButton'

// Unified spacing constants for consistent alignment
const SIDEBAR_SPACING = {
  base: 3, // 12px - base horizontal spacing for the sidebar
  nested: 3, // 12px - additional offset for nested items (relative to base)
}

interface ChatSideBarProps extends BoxProps {
  isLoadingDetails?: boolean
  onNewChat: () => void
  selectedConversationId?: string
  onSelectConversation: (id: string, agentId?: string) => void
  isSidebarOpenExternal?: boolean
  onSidebarToggle?: () => void
  isMinimized?: boolean
  onClearConversation?: () => void
  onSelectAgent?: (agentId: string) => void
}

// Helper function to determine which time period a chat belongs to
function getChatTimePeriod(timestamp: string): 'today' | 'thisWeek' | 'lastWeek' | 'earlier' {
  const chatDate = new Date(timestamp)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  // Set to beginning of day for accurate comparison
  today.setHours(0, 0, 0, 0)
  yesterday.setHours(0, 0, 0, 0)

  // Calculate start of this week (Monday)
  const thisWeekStart = new Date(today)
  const dayOfWeek = today.getDay() || 7 // Convert Sunday (0) to 7
  thisWeekStart.setDate(today.getDate() - dayOfWeek + 1)

  // Calculate start of last week
  const lastWeekStart = new Date(thisWeekStart)
  lastWeekStart.setDate(lastWeekStart.getDate() - 7)

  // Determine period
  if (chatDate >= today) {
    return 'today'
  } else if (chatDate >= thisWeekStart) {
    return 'thisWeek'
  } else if (chatDate >= lastWeekStart) {
    return 'lastWeek'
  } else {
    return 'earlier'
  }
}

const slugToIcon = (slug: string, iconColor: string, isDarkMode: boolean) => {
  switch (slug) {
    default:
      return null
  }
}

const getIcon = (chat: ChatSummarySchema, iconColor: string, isDarkMode: boolean) => {
  if (chat.agentIcon && !chat.agentSlug) {
    return getIconByName(chat.agentIcon)
  } else if (chat.agentSlug) {
    return slugToIcon(chat.agentSlug, iconColor, isDarkMode)
  }
  return null
}

const SESSION_STORAGE_KEYS = {
  SELECTED_TAB: 'timing_selected_tab',
}

const ChatSidebar: React.FC<ChatSideBarProps> = ({
  onNewChat,
  isLoadingDetails,
  selectedConversationId: selectedChatId,
  onSelectConversation: onSelectChat,
  isSidebarOpenExternal,
  onSidebarToggle,
  isMinimized,
  onClearConversation,
  onSelectAgent,
  ...props
}) => {
  const navigate = useNavigate()
  const isMobile = useMobile()
  const { isOpen: isOpenInternal, onClose: onCloseInternal } = useDisclosure({ defaultIsOpen: !isMobile })
  const [searchQuery, setSearchQuery] = useState('')
  // const { user } = useAppContext();
  const { t } = useLanguage()

  const { agentId } = useParams()

  // Use the unified chats hook with search functionality
  const {
    chatList: chats,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useChats({ searchQuery })

  const { agents, agentError, isLoadingAgents } = useAgents()
  const { user } = useAuth()

  // Color mode values
  const { colorMode } = useColorMode()
  const sidebarBg = useColorModeValue('sidebar.light', 'sidebar.dark')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const iconColor = useColorModeValue('gray.600', 'gray.300')
  const countColor = useColorModeValue('gray.500', 'gray.400')
  const placeholderColor = useColorModeValue('gray.500', 'gray.400')
  const isDarkMode = colorMode === 'dark'

  // Determine whether to use internal or external state management
  const isExternallyControlled = isSidebarOpenExternal !== undefined && onSidebarToggle !== undefined
  const isOpen = isExternallyControlled ? isSidebarOpenExternal : isOpenInternal

  // Close sidebar when a chat is selected on mobile
  useEffect(() => {
    if (isMobile && selectedChatId && !isExternallyControlled) {
      onCloseInternal()
    }
  }, [selectedChatId, isMobile, onCloseInternal, isExternallyControlled])

  // Auto-select internal data agent when agents are loaded and no agent is currently selected
  useEffect(() => {
    if (!isLoadingAgents && agents.length > 0 && !agentId && onSelectAgent) {
      // Prioritize TIPP agent first
      const internalDataAgent = agents.find(agent => agent.slug === 'internal_data' && agent.type === 'privileged')
      if (internalDataAgent) {
        onSelectAgent(internalDataAgent.id)
      } else {
        // Fallback to GPT agent if TIPP is not available
        const gptAgent = agents.find(agent => agent.slug === 'gpt' && agent.type === 'privileged')
        if (gptAgent) {
          onSelectAgent(gptAgent.id)
        }
      }
    }
  }, [isLoadingAgents, agents, agentId, onSelectAgent])

  const handleChatItemClick = useCallback(
    (chatId: string) => {
      const chat = chats.find(c => c.id === chatId)

      if (chat && chat?.agentId) {
        onSelectChat(chatId, chat.agentId)
      } else {
        // Legacy chat without agent
        onSelectChat(chatId)
      }
    },
    [onSelectChat, chats],
  )

  const handleAgentItemClick = useCallback(
    (agentClickedId: string) => {
      if (agentClickedId !== agentId) {
        onClearConversation?.()
        onSelectAgent?.(agentClickedId)
      }
    },
    [agentId, onClearConversation, onSelectAgent],
  )

  // Improved section mapping based on departments
  const agentSections = useMemo(() => {
    const sections = {
      favorites: [] as typeof agents,
      privileged: [] as typeof agents,
      departments: {} as Record<
        string,
        {
          agents: typeof agents
          icon_url?: string
        }
      >,
    }

    agents.forEach(agent => {
      // Add to favorites if preferred
      if (agent.is_preferred) {
        sections.favorites.push(agent)
      }

      // Add to privileged section
      if (agent.type === 'privileged') {
        sections.privileged.push(agent)
      } else {
        // Add to department sections for non-privileged agents
        const department = agent.department
        const departmentName = department?.name || 'Uncategorized'

        if (!sections.departments[departmentName]) {
          sections.departments[departmentName] = {
            agents: [],
            icon_url: department?.icon_url,
          }
        }
        sections.departments[departmentName].agents.push(agent)
      }
    })

    return sections
  }, [agents])

  // Group chats by time period
  const groupedChats = useMemo(() => {
    const groups = {
      today: [] as ChatSummarySchema[],
      thisWeek: [] as ChatSummarySchema[],
      lastWeek: [] as ChatSummarySchema[],
      earlier: [] as ChatSummarySchema[],
    }

    chats.forEach(chat => {
      const period = getChatTimePeriod(chat.timestamp)
      groups[period].push(chat)
    })

    // Sort chats within each group by timestamp (newest first)
    Object.keys(groups).forEach(key => {
      groups[key as keyof typeof groups].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      )
    })

    return groups
  }, [chats])

  // Get section title and count
  const getSectionTitle = useCallback(
    (section: string, count: number) => {
      switch (section) {
        case 'today':
          return `${t('sidebar.today')} (${count})`
        case 'thisWeek':
          return `${t('sidebar.thisWeek')} (${count})`
        case 'lastWeek':
          return `${t('sidebar.lastWeek')} (${count})`
        case 'earlier':
          return `${t('sidebar.earlier')} (${count})`
        default:
          return `${section} (${count})`
      }
    },
    [t],
  )

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }, [])

  const handleLibraryClick = useCallback(() => {
    navigate('/library')
  }, [navigate])

  // Tab selection state and persistence
  const [selectedTab, setSelectedTab] = useState<number>(() => {
    const storedTab = getFromSessionStorage(SESSION_STORAGE_KEYS.SELECTED_TAB)
    return storedTab ? parseInt(storedTab, 10) : 0
  })

  const selectTab = (tabIndex: number) => {
    setSelectedTab(tabIndex)
    setToSessionStorage(SESSION_STORAGE_KEYS.SELECTED_TAB, tabIndex.toString())
  }

  // Initialize intersection observer for infinite scroll using react-intersection-observer
  const { ref: inViewRef, inView } = useInView({
    threshold: 0.1,
    rootMargin: '200px',
  })

  const showAssistantsTab = false // TODO: make dynamic based on customer config
  // Adjusted tab index when Assistants is hidden and only Conversations is visible
  const getAdjustedTabIndex = () => {
    if (!showAssistantsTab) {
      return selectedTab === 1 ? 0 : 0
    }
    return selectedTab
  }

  // Handle tab change when Assistants is hidden and only Conversations is visible
  const handleTabChange = (index: number) => {
    if (!showAssistantsTab) {
      selectTab(1)
    } else {
      // Normal behavior when both tabs are visible
      selectTab(index)
    }
  }

  // Trigger fetchNextPage when the sentinel element comes into view
  React.useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

  return (
    <>
      <Flex
        {...props}
        as='aside'
        direction='column'
        h='100vh'
        pt={12} // Increased padding to make room for toggle button
        bg={sidebarBg}
        borderRight='1px'
        borderColor={borderColor}
        position='fixed'
        left={0}
        top={0}
        zIndex={2}
        overflow='hidden'
        w={isMobile ? '100vw' : '300px'}
        maxW={isMobile ? '100vw' : '300px'}
        minW={isMobile ? '100vw' : '300px'}
        transform={isOpen ? 'translateX(0)' : `translateX(-${isMobile ? '100vw' : '300px'})`}
        transition='transform 0.3s ease-in-out'
      >
        {/* Sidebar Toggle Button */}
        {onSidebarToggle && <SidebarToggleButton isOpen={isOpen} onToggle={onSidebarToggle} isFloating={false} />}
        <Button
          bg={CLIENT_CONFIG.theme.colors.primary}
          color='white'
          _hover={{
            bg: CLIENT_CONFIG.theme.colors.secondary,
          }}
          height='40px'
          display='flex'
          alignItems='center'
          justifyContent={isMinimized ? 'center' : 'flex-start'}
          onClick={onNewChat}
          isLoading={isLoading}
          loadingText={t('common.startingNewChat')}
          mx={SIDEBAR_SPACING.base}
          px={isMinimized ? 0 : 2}
        >
          <PlusIcon size={16} />
          {!isMinimized && <Text ml={2}>{t('common.newChat')}</Text>}
        </Button>

        <Tabs
          variant='soft-rounded'
          colorScheme='brand'
          size='sm'
          isFitted
          flex='1'
          display='flex'
          flexDirection='column'
          minH={0}
          index={getAdjustedTabIndex()}
          onChange={handleTabChange}
        >
          <TabList mx={SIDEBAR_SPACING.base} mb={4} mt={4}>
            {showAssistantsTab && (<Tab
              _selected={{
                color: 'white',
                bg: 'primary',
              }}
              _hover={{
                bg: 'primaryScheme.50',
                color: 'primary',
              }}
              fontSize='sm'
            >
              {t('sidebar.assistants')}
            </Tab>
            )}
            {/* DISABLED: History/Geschiedenis button - conversations always shown below
            <Tab
              _selected={{
                color: 'white',
                bg: 'primary',
              }}
              _hover={{
                bg: 'primaryScheme.50',
                color: 'primary',
              }}
              fontSize='sm'
            >
              {t('sidebar.conversations')}
            </Tab>
            */}
          </TabList>
          <TabPanels flex='1' minH={0} overflowY='auto'>
            {showAssistantsTab && <TabPanel p={0} mx={SIDEBAR_SPACING.base}>
              <VStack align='stretch' spacing={1}>
                {/* Privileged Agents - show as individual items like library */}
                {isLoadingAgents ? (
                  <Flex justify='center' align='center' py={4}>
                    <Spinner size='sm' color='primary' />
                    <Text ml={2} fontSize='sm' color='gray.500'>
                      {t('sidebar.loadingPrivilegedAgents', { defaultValue: 'Loading...' })}
                    </Text>
                  </Flex>
                ) : (
                  agentSections.privileged
                    .sort((a, b) => {
                      const order = { tipp: 0, gpt: 1, web: 2 }
                      return (
                        (order[a.slug as keyof typeof order] ?? 999) - (order[b.slug as keyof typeof order] ?? 999)
                      )
                    })
                    .map(agent => (
                      <SidebarItem
                        key={agent.id}
                        label={agent.name}
                        icon={slugToIcon(agent.slug || '', iconColor, isDarkMode)}
                        isSelected={agent.id === agentId}
                        onClick={() => handleAgentItemClick(agent.id)}
                        showLabel={!isMinimized}
                      />
                    ))
                )}
                <Divider />
                {/* FIXME: make dynamic based on customer config */}
                {/* Library Item - below web search */}
                {false && (
                  <SidebarItem
                    key='library'
                    label={t('library.library')}
                    icon={<FiGrid size={16} />}
                    isSelected={false}
                    onClick={handleLibraryClick}
                    showLabel={!isMinimized}
                  />
                )}

                {/* Agent Sections Loading/Error/Content */}
                {isLoadingAgents ? (
                  <Flex justify='center' align='center' py={8}>
                    <Spinner thickness='4px' speed='0.65s' emptyColor='gray.200' color='primary' size='xl' />
                  </Flex>
                ) : agentError ? (
                  <Text py={2} color='red.500'>
                    {agentError}
                  </Text>
                ) : agents && agents.length > 0 ? (
                  <>
                    {/* FIXME: make dynamic based on customer config */}
                    {false && (
                      <React.Fragment>
                        {/* Favorites Section Header */}
                        <Flex align='center' py={2} mb={2}>
                          <Box mr={2}>
                            <Star
                              size={16}
                              color='var(--chakra-colors-yellow-500)'
                              fill='var(--chakra-colors-yellow-500)'
                            />
                          </Box>
                          <Text fontSize='sm' fontWeight='semibold' color='yellow.600'>
                            {t('sidebar.favorites', { defaultValue: 'Favorieten' })}
                          </Text>
                          <Text fontSize='xs' color={countColor} ml={2}>
                            ({agentSections.favorites.length})
                          </Text>
                        </Flex>

                        {/* Favorites Section - Non-expandable */}
                        {agentSections.favorites.length > 0 ? (
                          <VStack align='stretch' spacing={1} mb={4}>
                            {[...agentSections.favorites]
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map(agent => (
                              <SidebarItem
                                key={agent.id}
                                label={agent.name}
                                icon={getIconByName(agent.profile_picture_url)}
                                isSelected={agent.id === agentId}
                                onClick={() => handleAgentItemClick(agent.id)}
                                showLabel={!isMinimized}
                              />
                            ))}
                          </VStack>
                        ) : (
                          <Box p={3} bg='gray.50' borderRadius='md' border='1px dashed' borderColor='gray.300' mb={4}>
                            <Flex align='center' mb={2}>
                              <Heart size={14} color='var(--chakra-colors-gray-400)' />
                              <Text fontSize='xs' color='gray.600' ml={2} fontWeight='medium'>
                                {t('sidebar.noFavorites', { defaultValue: 'Geen favorieten' })}
                              </Text>
                            </Flex>
                            <Text fontSize='xs' color='gray.500' lineHeight='1.4'>
                              {t('sidebar.addFavoritesHint', {
                                defaultValue: 'Ga naar de bibliotheek om assistenten toe te voegen aan je favorieten.',
                              })}
                            </Text>
                          </Box>
                        )}
                      </React.Fragment>
                    )}
                  </>
                ) : (
                  <Text py={2}>{t('sidebar.noAssistantsAvailable')}</Text>
                )}
              </VStack>
            </TabPanel>}
            {/* Chat History Panel */}
            <TabPanel p={0} mx={SIDEBAR_SPACING.base} overflowY='auto' flex='1'>
              <VStack align='stretch' spacing={1}>
                {/* Search input */}
                <InputGroup size='md' mb={4}>
                  <InputLeftElement pointerEvents='none'>
                    <FiSearch color='gray.500' />
                  </InputLeftElement>
                  <Input
                    placeholder={t('sidebar.searchConversations', { defaultValue: 'Search conversations...' } as {
                      defaultValue: string
                    })}
                    value={searchQuery}
                    onChange={handleSearchChange}
                    borderRadius='md'
                    borderColor='gray.300'
                    _placeholder={{
                      color: placeholderColor,
                    }}
                    _focus={{
                      borderColor: 'primary',
                      boxShadow: '0 0 0 1px var(--chakra-colors-primary)',
                    }}
                  />
                </InputGroup>

                {isLoading ? (
                  <Flex justify='center' align='center' height='100px'>
                    <Spinner size='md' color='primary' />
                  </Flex>
                ) : !error && chats.length === 0 && searchQuery.trim().length === 0 ? (
                  <Text color='secondary' mb={4}>
                    {t('sidebar.noConversationsAvailable', { defaultValue: 'No conversations available' } as {
                      defaultValue: string
                    })}
                  </Text>
                ) : !error && chats.length === 0 && searchQuery.trim().length > 0 ? (
                  <Text color='secondaryAlpha'>
                    {t('sidebar.noSearchResults', { defaultValue: 'No conversations matching your search' } as {
                      defaultValue: string
                    })}
                  </Text>
                ) : (
                  <React.Fragment>
                    {/* Today's chats */}
                    {groupedChats.today.length > 0 && (
                      <SidebarSection title={getSectionTitle('today', groupedChats.today.length)}>
                        {groupedChats.today.map((chat: ChatSummarySchema) => (
                            <SidebarItem
                              key={chat.id}
                              icon={getIcon(chat, iconColor, isDarkMode) || <FiMessageSquare size={16} />}
                              label={chat.title || t('sidebar.conversation')}
                              onClick={() => handleChatItemClick(chat.id)}
                              isSelected={selectedChatId === chat.id}
                              showLabel={!isMinimized}
                              compact={true}
                            />
                        ))}
                      </SidebarSection>
                    )}
                    {/* This Week's chats */}
                    {groupedChats.thisWeek.length > 0 && (
                      <SidebarSection title={getSectionTitle('thisWeek', groupedChats.thisWeek.length)}>
                        {groupedChats.thisWeek.map((chat: ChatSummarySchema) => (
                            <SidebarItem
                              key={chat.id}
                              icon={getIcon(chat, iconColor, isDarkMode) || <FiMessageSquare size={16} />}
                              label={chat.title || t('sidebar.conversation')}
                              onClick={() => handleChatItemClick(chat.id)}
                              isSelected={selectedChatId === chat.id}
                              showLabel={!isMinimized}
                              compact={true}
                            />
                        ))}
                      </SidebarSection>
                    )}
                    {/* Last Week's chats */}
                    {groupedChats.lastWeek.length > 0 && (
                      <SidebarSection title={getSectionTitle('lastWeek', groupedChats.lastWeek.length)}>
                        {groupedChats.lastWeek.map((chat: ChatSummarySchema) => (
                            <SidebarItem
                              key={chat.id}
                              icon={getIcon(chat, iconColor, isDarkMode) || <FiMessageSquare size={16} />}
                              label={chat.title || t('sidebar.conversation')}
                              onClick={() => handleChatItemClick(chat.id)}
                              isSelected={selectedChatId === chat.id}
                              showLabel={!isMinimized}
                              compact={true}
                            />
                        ))}
                      </SidebarSection>
                    )}
                    {/* Earlier chats */}
                    {groupedChats.earlier.length > 0 && (
                      <SidebarSection title={getSectionTitle('earlier', groupedChats.earlier.length)}>
                        {groupedChats.earlier.map((chat: ChatSummarySchema) => (
                            <SidebarItem
                              key={chat.id}
                              icon={getIcon(chat, iconColor, isDarkMode) || <FiMessageSquare size={16} />}
                              label={chat.title || t('sidebar.conversation')}
                              onClick={() => handleChatItemClick(chat.id)}
                              isSelected={selectedChatId === chat.id}
                              showLabel={!isMinimized}
                              compact={true}
                            />
                        ))}
                      </SidebarSection>
                    )}

                    {/* Invisible sentinel element for infinite scroll */}
                    {hasNextPage && <Box ref={inViewRef} height='1px' />}

                    {/* Loading indicator for infinite scroll */}
                    {isFetchingNextPage && (
                      <Flex justify='center' align='center' py={4}>
                        <Spinner size='sm' color='primary' />
                        <Text ml={2} fontSize='sm' color='gray.500'>
                          {t('sidebar.loadingMoreConversations', { defaultValue: 'Loading more conversations...' })}
                        </Text>
                      </Flex>
                    )}
                  </React.Fragment>
                )}
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
        <Divider />
        <Box mt='auto' mx={SIDEBAR_SPACING.base} pb={4}>
          <VStack spacing={2}>
            <Box width='full'>
              <SidebarItem
                icon={
                  <Avatar
                    size='xs'
                    name={user?.displayName || t('chat.anonymousUser') || 'Anonymous User'}
                    src={user?.avatar}
                    bg='primary'
                    color='white'
                  />
                }
                label={user?.displayName || t('chat.anonymousUser') || 'Anonymous User'}
                onClick={() => navigate('/settings')}
                showLabel={!isMinimized}
              />
            </Box>
          </VStack>
        </Box>
      </Flex>
    </>
  )
}

export default React.memo(ChatSidebar)
